// components/Pages/RM/Inventory/InventoryListing.jsx
import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    fetchAllInventoryAction,
    updateThresholdsAction,
    updateLocationAction
} from '../../../../utils/redux/inventory/inventoryActions';
import { fetchAllRawMaterialAction } from '../../../../utils/redux/rawMaterial/rawMaterialActions';
import { fetchAllRawMaterialCategoryAction } from '../../../../utils/redux/rawMaterialCategory/rawMaterialCategoryActions';
import {
    fetchRawMaterialVendors,
    fetchCategoryTableColumns
} from '../../../../utils/api-service/get-services';
import { IoIosArrowDown } from 'react-icons/io';
import { FaEdit, FaEye, FaExclamationTriangle, FaSync } from 'react-icons/fa';
import { toast } from 'react-toastify';
import InventoryTransactionModal from './InventoryTransactionModal';
import ThresholdModal from './ThresholdModal';
import LocationModal from './LocationModal';
import SyncInventoryModal from './SyncInventoryModal';
import { postMethod } from '../../../../utils/api-service/api-service';

const InventoryListing = () => {
    const dispatch = useDispatch();
    const { inventory, stats, isLoading, total, pages, limit } = useSelector(state => state.inventory);
    const { rawMaterials } = useSelector(state => state.rawMaterial);
    const { rawMaterialCategories } = useSelector(state => state.rawMaterialCategory);

    const [openRow, setOpenRow] = useState(null);
    const [vendors, setVendors] = useState([]);
    const [vendorsLoading, setVendorsLoading] = useState(false);
    const [categoryConfig, setCategoryConfig] = useState(null);

    // Cache of vendorId -> Map(columnHeader -> propertyDef)
    const [allVendorPropertyMaps, setAllVendorPropertyMaps] = useState({});

    const [filters, setFilters] = useState({
        category: '',
        vendor: '',
        search: '',
        status: '',
        warehouse: '',
        lowStockOnly: false,
    });

    const [showTransactionModal, setShowTransactionModal] = useState(false);
    const [showThresholdModal, setShowThresholdModal] = useState(false);
    const [showLocationModal, setShowLocationModal] = useState(false);
    const [showSyncModal, setShowSyncModal] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);

    // Load categories + raw materials once
    useEffect(() => {
        dispatch(fetchAllRawMaterialCategoryAction());
        dispatch(fetchAllRawMaterialAction());
    }, [dispatch]);

    // Load vendors + table column config when category changes
    useEffect(() => {
        if (!filters.category) {
            setVendors([]);
            setCategoryConfig(null);
            return;
        }

        (async () => {
            try {
                setVendorsLoading(true);

                // Vendors
                const vendorRes = await fetchRawMaterialVendors("", filters.category);
                const vendorList = vendorRes?.vendors || [];
                setVendors(vendorList);

                // Category table column config
                const configRes = await fetchCategoryTableColumns(filters.category);
                if (configRes?.success) setCategoryConfig(configRes.data);

                // Build a lookup: vendorId -> Map(columnHeader -> propertyDef)
                // This lets us know which vendor property to pull for each column
                const propertyMap = {};
                vendorList.forEach(v => {
                    const innerMap = {};
                    (v.vendorItemPropertyDefinitions || []).forEach(def => {
                        if (def.mappedToColumn) {
                            innerMap[def.mappedToColumn] = def;
                        }
                    });
                    propertyMap[v._id] = innerMap;
                });
                setAllVendorPropertyMaps(propertyMap);
            } catch (err) {
                console.error('Failed to load category config / vendors:', err);
                setVendors([]);
                setCategoryConfig(null);
                setAllVendorPropertyMaps({});
            } finally {
                setVendorsLoading(false);
            }
        })();
    }, [filters.category]);

    // Reload data when filters or page changes
    useEffect(() => {
        loadData();
    }, [filters, currentPage]);

    const loadData = async () => {
        await dispatch(fetchAllInventoryAction({
            ...filters,
            page: currentPage,
            limit: 20
        }));
    };

    // -------------------- DYNAMIC COLUMNS --------------------
    const getCategoryColumns = useCallback(() => {
        if (!categoryConfig) return [];
        // Show columns if displaySettings allows; fallback to showing when columns exist
        const showVendorCols =
            categoryConfig.displaySettings?.showVendorPropertiesInTable !== false;
        if (!showVendorCols) return [];

        return (categoryConfig.tableColumns || [])
            .filter(col => col.isVisible !== false)
            .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0));
    }, [categoryConfig]);

    // Get value of a dynamic column for an inventory item
    const getColumnValue = (item, column) => {
        const raw = item.rawMaterial || {};
        const vendorId = raw.vendor?._id || raw.vendor;
        if (!vendorId) return '—';

        const propertyMap = allVendorPropertyMaps[vendorId] || {};
        const def = propertyMap[column.header];
        if (!def) return '—';

        const value = raw.vendorProperties?.[def.name];
        if (value === undefined || value === null || value === '') return '—';
        if (Array.isArray(value)) return value.join(', ');
        if (typeof value === 'object') return JSON.stringify(value);
        return String(value);
    };

    // -------------------- FILTER HANDLERS --------------------
    const handleCategoryChange = (e) => {
        const value = e.target.value;
        setFilters(prev => ({
            ...prev,
            category: value,
            vendor: '',
            search: '',
        }));
        setCurrentPage(1);
        setOpenRow(null);
    };

    const handleVendorChange = (e) => {
        const value = e.target.value;
        setFilters(prev => ({ ...prev, vendor: value, search: '' }));
        setCurrentPage(1);
    };

    const handleOtherFilterChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFilters(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
        setCurrentPage(1);
    };

    const handleClearFilters = () => {
        setFilters({
            category: '',
            vendor: '',
            search: '',
            status: '',
            warehouse: '',
            lowStockOnly: false
        });
        setCurrentPage(1);
    };

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= pages) setCurrentPage(newPage);
    };

    // -------------------- MODAL HANDLERS --------------------
    const handleThresholdUpdate = async (data) => {
        try {
            const response = await dispatch(updateThresholdsAction(selectedItem.rawMaterial._id, data));
            if (response?.success) {
                toast.success('Thresholds updated successfully');
                setShowThresholdModal(false);
                await loadData();
            }
        } catch (error) {
            toast.error('Failed to update thresholds');
        }
    };

    const handleLocationUpdate = async (data) => {
        try {
            const response = await dispatch(updateLocationAction(selectedItem.rawMaterial._id, data));
            if (response?.success) {
                toast.success('Location updated successfully');
                setShowLocationModal(false);
                await loadData();
            }
        } catch (error) {
            toast.error('Failed to update location');
        }
    };

    const handleSyncInventory = async () => {
        try {
            const response = await postMethod('/inventory/sync');
            if (response?.success) {
                toast.success(response.message);
                await loadData();
                setShowSyncModal(false);
            } else {
                toast.error(response?.message || 'Sync failed');
            }
        } catch (error) {
            toast.error('Failed to sync inventory');
        }
    };

    // -------------------- HELPERS --------------------
    const getStatusBadge = (status) => {
        const badges = {
            'IN_STOCK': 'success',
            'LOW_STOCK': 'warning',
            'OUT_OF_STOCK': 'danger',
            'DISCONTINUED': 'secondary'
        };
        return badges[status] || 'primary';
    };

    const getCategoryName = (categoryId) => {
        if (categoryId && typeof categoryId === 'object') return categoryId.name || 'Uncategorized';
        const category = rawMaterialCategories.find(c => c._id === categoryId);
        return category?.name || 'Uncategorized';
    };

    const formatPropertyKey = (key) =>
        key.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase()).trim();

    const formatPropertyValue = (value) => {
        if (value === null || value === undefined || value === '') return '—';
        if (Array.isArray(value)) return value.join(', ');
        if (typeof value === 'object') return JSON.stringify(value);
        return String(value);
    };

    // -------------------- RENDER: FILTERS --------------------
    const renderFilters = () => {
        const categorySelected = !!filters.category;
        const isFabricCategory = rawMaterialCategories
            .find(c => c._id === filters.category)?.slug === 'fabric';

        return (
            <div className="card mb-4">
                <div className="card-body">
                    <div className="row g-3">
                        {/* 1. CATEGORY */}
                        <div className="col-md-3">
                            <label className="form-label fw-bold">1. Category</label>
                            <select
                                className="form-select"
                                name="category"
                                value={filters.category}
                                onChange={handleCategoryChange}
                            >
                                <option value="">Select Category</option>
                                {rawMaterialCategories.map(cat => (
                                    <option key={cat._id} value={cat._id}>{cat.name}</option>
                                ))}
                            </select>
                        </div>

                        {/* 2. VENDOR */}
                        <div className="col-md-3">
                            <label className="form-label fw-bold">2. Vendor</label>
                            <select
                                className="form-select"
                                name="vendor"
                                value={filters.vendor}
                                onChange={handleVendorChange}
                                disabled={!categorySelected || vendorsLoading}
                            >
                                <option value="">
                                    {vendorsLoading
                                        ? 'Loading vendors...'
                                        : categorySelected
                                            ? 'All Vendors'
                                            : 'Select Category first'}
                                </option>
                                {vendors.map(v => (
                                    <option key={v._id} value={v._id}>{v.name}</option>
                                ))}
                            </select>
                        </div>

                        {/* 3. SEARCH */}
                        <div className="col-md-3">
                            <label className="form-label fw-bold">3. Search</label>
                            <input
                                type="text"
                                className="form-control"
                                placeholder={
                                    !categorySelected
                                        ? 'Select Category first'
                                        : isFabricCategory
                                            ? 'Search design, shade, quality...'
                                            : 'Search by name or code...'
                                }
                                name="search"
                                value={filters.search}
                                onChange={handleOtherFilterChange}
                                disabled={!categorySelected}
                            />
                        </div>

                        {/* STATUS */}
                        <div className="col-md-2">
                            <label className="form-label fw-bold">Status</label>
                            <select
                                className="form-select"
                                name="status"
                                value={filters.status}
                                onChange={handleOtherFilterChange}
                            >
                                <option value="">All</option>
                                <option value="IN_STOCK">In Stock</option>
                                <option value="LOW_STOCK">Low Stock</option>
                                <option value="OUT_OF_STOCK">Out of Stock</option>
                            </select>
                        </div>

                        {/* LOW STOCK ONLY */}
                        <div className="col-md-1 d-flex align-items-end">
                            <div className="form-check mb-2">
                                <input
                                    type="checkbox"
                                    className="form-check-input"
                                    id="lowStockOnly"
                                    name="lowStockOnly"
                                    checked={filters.lowStockOnly}
                                    onChange={handleOtherFilterChange}
                                />
                                <label className="form-check-label" htmlFor="lowStockOnly">
                                    Low Only
                                </label>
                            </div>
                        </div>

                        <div className="col-12 d-flex justify-content-end">
                            <button className="btn btn-secondary" onClick={handleClearFilters}>
                                Clear All
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    // -------------------- RENDER: EMPTY STATE --------------------
    const renderEmptyState = () => (
        <div className="text-center py-5">
            <div className="mb-4">
                <FaSync size={48} className="text-muted" />
            </div>
            <h4>No Inventory Items Found</h4>
            <p className="text-muted">
                {!filters.category
                    ? 'Select a category above to view inventory.'
                    : 'No items match the current filters.'}
            </p>
            {!filters.category && rawMaterials && rawMaterials.length > 0 && (
                <button
                    className="btn btn-primary"
                    onClick={() => setShowSyncModal(true)}
                >
                    <FaSync className="me-2" />
                    Sync Inventory
                </button>
            )}
        </div>
    );

    // -------------------- RENDER: EXPANDED DETAILS --------------------
    const renderExpandedDetails = (item) => {
        const rawMaterial = item.rawMaterial || {};
        const materialProperties = rawMaterial.properties || {};
        const vendorProperties = rawMaterial.vendorProperties || {};
        const hasMaterialProps = Object.keys(materialProperties).length > 0;
        const hasVendorProps = Object.keys(vendorProperties).length > 0;

        const vendorDefs = rawMaterial.vendor?.vendorItemPropertyDefinitions || [];
        const vendorDefMap = new Map();
        vendorDefs.forEach(def => vendorDefMap.set(def.name, def));

        return (
            <tr>
                <td colSpan={100} className="bg-light p-3">
                    <div className="row g-3">
                        {/* COLUMN 1: Stock Information */}
                        <div className="col-md-4">
                            <h6 className="mb-3">📊 Stock Information</h6>
                            <table className="table table-sm table-bordered mb-0">
                                <tbody>
                                    <tr>
                                        <td className="fw-semibold">Current Stock</td>
                                        <td>{item.currentStock?.quantity} {item.currentStock?.unit}</td>
                                    </tr>
                                    <tr>
                                        <td className="fw-semibold">Min Stock</td>
                                        <td>{item.thresholds?.minStock ?? 0}</td>
                                    </tr>
                                    <tr>
                                        <td className="fw-semibold">Max Stock</td>
                                        <td>{item.thresholds?.maxStock ?? 'Unlimited'}</td>
                                    </tr>
                                    <tr>
                                        <td className="fw-semibold">Reorder Point</td>
                                        <td>{item.thresholds?.reorderPoint ?? 0}</td>
                                    </tr>
                                    <tr>
                                        <td className="fw-semibold">Reorder Qty</td>
                                        <td>{item.thresholds?.reorderQuantity ?? 0}</td>
                                    </tr>
                                    <tr>
                                        <td className="fw-semibold">Average Cost</td>
                                        <td>₹{item.averageCost?.toFixed(2) ?? '0.00'}</td>
                                    </tr>
                                    <tr>
                                        <td className="fw-semibold">Total Value</td>
                                        <td>₹{item.totalValue?.toFixed(2) ?? '0.00'}</td>
                                    </tr>
                                    <tr>
                                        <td className="fw-semibold">Last Updated</td>
                                        <td>
                                            {item.currentStock?.lastUpdated
                                                ? new Date(item.currentStock.lastUpdated).toLocaleString()
                                                : '—'}
                                        </td>
                                    </tr>
                                </tbody>
                            </table>

                            <h6 className="mb-3 mt-3">📍 Location</h6>
                            <table className="table table-sm table-bordered mb-0">
                                <tbody>
                                    <tr>
                                        <td className="fw-semibold">Warehouse</td>
                                        <td>{item.location?.warehouse || '—'}</td>
                                    </tr>
                                    <tr>
                                        <td className="fw-semibold">Shelf</td>
                                        <td>{item.location?.shelf || '—'}</td>
                                    </tr>
                                    <tr>
                                        <td className="fw-semibold">Bin</td>
                                        <td>{item.location?.bin || '—'}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>

                        {/* COLUMN 2: Material Properties + Pricing */}
                        <div className="col-md-4">
                            <h6 className="mb-3">📦 Material Properties</h6>
                            {hasMaterialProps ? (
                                <table className="table table-sm table-bordered mb-0">
                                    <tbody>
                                        {Object.entries(materialProperties).map(([key, value]) => (
                                            <tr key={key}>
                                                <td className="fw-semibold">{formatPropertyKey(key)}</td>
                                                <td>{formatPropertyValue(value)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            ) : (
                                <div className="text-muted small">No material properties defined</div>
                            )}

                            <h6 className="mb-3 mt-3">💰 Pricing</h6>
                            <table className="table table-sm table-bordered mb-0">
                                <tbody>
                                    <tr>
                                        <td className="fw-semibold">Cost Price</td>
                                        <td>
                                            {rawMaterial.currency || 'INR'}{' '}
                                            {rawMaterial.cost_price_per_unit ?? '—'}
                                        </td>
                                    </tr>
                                    <tr>
                                        <td className="fw-semibold">Selling Price</td>
                                        <td>
                                            {rawMaterial.currency || 'INR'}{' '}
                                            {rawMaterial.selling_price_per_unit ?? '—'}
                                        </td>
                                    </tr>
                                    <tr>
                                        <td className="fw-semibold">Unit</td>
                                        <td>{rawMaterial.unit || '—'}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>

                        {/* COLUMN 3: Vendor Properties */}
                        <div className="col-md-4">
                            <h6 className="mb-3">
                                🏢 Vendor Properties
                                {rawMaterial.vendor?.name && (
                                    <span className="text-muted ms-2 small">
                                        ({rawMaterial.vendor.name})
                                    </span>
                                )}
                            </h6>
                            {hasVendorProps ? (
                                <table className="table table-sm table-bordered mb-0">
                                    <tbody>
                                        {Object.entries(vendorProperties).map(([key, value]) => {
                                            const def = vendorDefMap.get(key);
                                            return (
                                                <tr key={key}>
                                                    <td className="fw-semibold">
                                                        {def?.label || formatPropertyKey(key)}
                                                    </td>
                                                    <td>{formatPropertyValue(value)}</td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            ) : (
                                <div className="text-muted small">No vendor properties defined</div>
                            )}
                        </div>
                    </div>
                </td>
            </tr>
        );
    };

    // -------------------- RENDER: TABLE --------------------
    const renderTable = () => {
        const dynamicColumns = getCategoryColumns();
        const totalColCount = 10 + dynamicColumns.length;

        return (
            <div
                className="table-responsive"
                style={{
                    overflowX: 'auto',
                    width: '100%',
                    border: '1px solid #dee2e6',
                    borderRadius: 6,
                }}
            >
                <table
                    className="table table-sm align-middle mt-0 mb-0"
                    style={{ minWidth: 1200 + dynamicColumns.length * 140 }}
                >
                    <thead className="table-secondary" style={{ position: 'sticky', top: 0, zIndex: 2 }}>
                        <tr>
                            <th className="p-3" style={{ width: 40, minWidth: 40 }}></th>
                            <th className="p-3" style={{ minWidth: 180 }}>Material</th>
                            <th className="p-3" style={{ minWidth: 120 }}>Code</th>
                            <th className="p-3" style={{ minWidth: 140 }}>Category</th>
                            {dynamicColumns.map((col, idx) => (
                                <th key={idx} className="p-3" style={{ minWidth: 140 }}>
                                    {col.header}
                                </th>
                            ))}
                            <th className="p-3 text-center" style={{ minWidth: 100 }}>Quantity</th>
                            <th className="p-3 text-center" style={{ minWidth: 80 }}>Unit</th>
                            <th className="p-3 text-center" style={{ minWidth: 100 }}>Avg Cost</th>
                            <th className="p-3 text-center" style={{ minWidth: 120 }}>Total Value</th>
                            <th className="p-3" style={{ minWidth: 130 }}>Status</th>
                            <th className="p-3" style={{ minWidth: 160 }}>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {isLoading ? (
                            <tr>
                                <td colSpan={totalColCount} className="text-center py-4">
                                    <div className="spinner-border text-primary" role="status">
                                        <span className="visually-hidden">Loading...</span>
                                    </div>
                                </td>
                            </tr>
                        ) : !inventory || inventory.length === 0 ? (
                            <tr>
                                <td colSpan={totalColCount} className="text-center py-4">
                                    {renderEmptyState()}
                                </td>
                            </tr>
                        ) : (
                            inventory.map((item, index) => (
                                <React.Fragment key={item._id}>
                                    <tr
                                        style={{ cursor: "pointer" }}
                                        onClick={() => setOpenRow(openRow === index ? null : index)}
                                    >
                                        <td className="p-3">
                                            <span style={{
                                                display: "inline-block",
                                                transform: openRow === index ? "rotate(180deg)" : "rotate(0deg)",
                                                transition: "transform 0.2s ease"
                                            }}>
                                                <IoIosArrowDown />
                                            </span>
                                        </td>
                                        <td className="p-3">{item.rawMaterial?.name}</td>
                                        <td className="p-3">{item.rawMaterial?.code}</td>
                                        <td className="p-3">{getCategoryName(item.rawMaterial?.category)}</td>
                                        {dynamicColumns.map((col, colIdx) => (
                                            <td key={colIdx} className="p-3">{getColumnValue(item, col)}</td>
                                        ))}
                                        <td className="p-3 text-center fw-bold">{item.currentStock.quantity}</td>
                                        <td className="p-3 text-center">{item.currentStock.unit}</td>
                                        <td className="p-3 text-center">₹{item.averageCost?.toFixed(2)}</td>
                                        <td className="p-3 text-center">₹{item.totalValue?.toFixed(2)}</td>
                                        <td className="p-3">
                                            <span className={`badge bg-${getStatusBadge(item.status)}`}>
                                                {item.status?.replace('_', ' ')}
                                            </span>
                                            {item.currentStock.quantity <= item.thresholds.reorderPoint && item.currentStock.quantity > 0 && (
                                                <span className="badge bg-warning ms-1" title="Low Stock">
                                                    <FaExclamationTriangle />
                                                </span>
                                            )}
                                        </td>
                                        <td className="p-3">
                                            <button
                                                className="btn btn-sm btn-success me-1"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setSelectedItem(item);
                                                    setShowTransactionModal(true);
                                                }}
                                                title="Add Transaction"
                                            >
                                                <FaEdit />
                                            </button>
                                            <button
                                                className="btn btn-sm btn-info me-1"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setSelectedItem(item);
                                                    setShowThresholdModal(true);
                                                }}
                                                title="Update Thresholds"
                                            >
                                                <FaEye />
                                            </button>
                                            <button
                                                className="btn btn-sm btn-warning"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setSelectedItem(item);
                                                    setShowLocationModal(true);
                                                }}
                                                title="Update Location"
                                            >
                                                📍
                                            </button>
                                        </td>
                                    </tr>
                                    {openRow === index && renderExpandedDetails(item)}
                                </React.Fragment>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
        );
    };

    // -------------------- RENDER: PAGINATION --------------------
    const renderPagination = () => {
        if (!inventory || inventory.length === 0) return null;

        const totalPages = Math.ceil(total / limit);
        if (totalPages <= 1) return null;

        const maxVisible = 5;
        let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
        let endPage = Math.min(totalPages, startPage + maxVisible - 1);

        if (endPage - startPage + 1 < maxVisible) {
            startPage = Math.max(1, endPage - maxVisible + 1);
        }

        const pageNumbers = [];
        for (let i = startPage; i <= endPage; i++) pageNumbers.push(i);

        return (
            <nav className="mt-4">
                <ul className="pagination justify-content-center">
                    <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                        <button
                            className="page-link"
                            onClick={() => handlePageChange(currentPage - 1)}
                            disabled={currentPage === 1}
                        >
                            Previous
                        </button>
                    </li>

                    {pageNumbers.map((pageNum) => (
                        <li key={pageNum} className={`page-item ${currentPage === pageNum ? 'active' : ''}`}>
                            <button
                                className="page-link"
                                onClick={() => handlePageChange(pageNum)}
                            >
                                {pageNum}
                            </button>
                        </li>
                    ))}

                    <li className={`page-item ${currentPage >= totalPages ? 'disabled' : ''}`}>
                        <button
                            className="page-link"
                            onClick={() => handlePageChange(currentPage + 1)}
                            disabled={currentPage >= totalPages}
                        >
                            Next
                        </button>
                    </li>
                </ul>
                <div className="text-center text-muted small">
                    Showing {((currentPage - 1) * limit) + 1} - {Math.min(currentPage * limit, total)} of {total} items
                </div>
            </nav>
        );
    };

    // -------------------- MAIN RENDER --------------------
    return (
        <>
            <div className="d-flex flex-column align-items-center">
                <div className="d-flex my-4 w-100 justify-content-between align-items-center">
                    <h3>📦 Inventory Management</h3>
                    <div>
                        <button
                            className="btn btn-outline-primary me-2"
                            onClick={() => setShowSyncModal(true)}
                        >
                            <FaSync className="me-2" />
                            Sync Inventory
                        </button>
                        <button
                            className="btn btn-primary"
                            onClick={() => {
                                setSelectedItem(null);
                                setShowTransactionModal(true);
                            }}
                        >
                            + Add Transaction
                        </button>
                    </div>
                </div>

                <div className="w-100">
                    {renderFilters()}
                </div>

                <div className="w-100">
                    {renderTable()}
                </div>

                {renderPagination()}
            </div>

            {/* Modals */}
            <InventoryTransactionModal
                show={showTransactionModal}
                onHide={() => setShowTransactionModal(false)}
                inventoryItem={selectedItem}
                onSuccess={() => loadData()}
            />

            <ThresholdModal
                show={showThresholdModal}
                onHide={() => setShowThresholdModal(false)}
                inventoryItem={selectedItem}
                onSave={handleThresholdUpdate}
            />

            <LocationModal
                show={showLocationModal}
                onHide={() => setShowLocationModal(false)}
                inventoryItem={selectedItem}
                onSave={handleLocationUpdate}
            />

            <SyncInventoryModal
                show={showSyncModal}
                onHide={() => setShowSyncModal(false)}
                onSync={handleSyncInventory}
                rawMaterialCount={rawMaterials?.length || 0}
                inventoryCount={inventory?.length || 0}
            />
        </>
    );
};

export default InventoryListing;